import React, { useState, useEffect } from "react";
import { Row, Col, Input, Button } from "antd";
import { DeleteOutlined } from "@ant-design/icons";

const FinanceForm = ({
  editable,
  onDataChange,
  initialData,
  emiRows,
  setEmiRows,
}) => {
  const initialState = [{ id: 1, tenure: null, emi: "" }];

  useEffect(() => {
    if (initialData) {
      try {
        const parsedData = JSON.parse(initialData);
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          const populatedRows = parsedData.map((item, index) => ({
            id: index + 1,
            tenure: item.tenure || "",
            emi: item.emi || "",
          }));
          setEmiRows(populatedRows);
        }
      } catch (error) {
        console.error("Error parsing initialData:", error);
      }
    } else {
      setEmiRows(initialState);
    }
  }, [initialData]);

  const updateParent = (rows) => {
    const payload = {
      data: rows
        .filter((row) => row.tenure && row.emi)
        .map(({ tenure, emi }) => ({ tenure, emi: Number(emi) })),
    };
    onDataChange(payload);
  };

  const addRow = () => {
    const updatedRows = [...emiRows, { id: Date.now(), tenure: null, emi: "" }];
    setEmiRows(updatedRows);
    updateParent(updatedRows);
  };

  const handleInputChange = (id, field, value) => {
    setEmiRows((prevRows) => {
      const updatedRows = prevRows.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      );

      const currentRowIndex = updatedRows.findIndex((row) => row.id === id);
      const isLastRow = currentRowIndex === updatedRows.length - 1;
      const currentRow = updatedRows[currentRowIndex];

      // Add row only if editing the last row and something has been entered
      if (editable && isLastRow && (currentRow.tenure || currentRow.emi)) {
        updatedRows.push({ id: Date.now(), tenure: null, emi: "" });
      }

      // Push updated data to parent
      updateParent(updatedRows);
      return updatedRows;
    });
  };

  const removeRow = (id) => {
    let updatedRows = emiRows.filter((row) => row.id !== id);
    if (updatedRows.length === 0) {
      updatedRows = initialState;
    }
    setEmiRows(updatedRows);
    updateParent(updatedRows);
  };

  return (
    <>
      <Row gutter={8} style={{ fontWeight: "bold", marginBottom: "5px" }}>
        <Col xs={12} md={10}>
          Tenure
        </Col>
        <Col xs={12} md={10}>
          EMI
        </Col>
        <Col xs={0} md={4}></Col>
      </Row>

      {emiRows.map((row) => (
        <Row
          key={row.id}
          gutter={8}
          align="middle"
          style={{ marginBottom: "5px" }}
        >
       <Col xs={24} md={10}>
  <Input
    type="number"
    value={row.tenure}
    onChange={(e) => {
      // Remove 'e' character and any non-numeric characters
      let value = e.target.value.replace(/[^0-9]/g, '');
      
      // Convert to number for validation
      const numValue = value ? Number(value) : "";
      
      // Validate range
      if (numValue === "" || (numValue >= 1 && numValue <= 60)) {
        handleInputChange(row.id, "tenure", numValue);
      }
    }}
    onKeyDown={(e) => {
      // Prevent 'e', 'E', '+', '-', '.' characters
      if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-' || e.key === '.') {
        e.preventDefault();
      }
    }}
    min="1"
    max="60"
    step="1"
    placeholder="Enter Tenure (1-60 months)"
    style={{ width: "100%" }}
    disabled={!editable}
  />
</Col>

        <Col xs={24} md={10}>
  <Input
    type="number"
    value={row.emi}
    onChange={(e) => {
      // Allow numbers and single decimal point
      let value = e.target.value.replace(/[^0-9.]/g, '');
      
      // Ensure only one decimal point
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Convert to number or empty string
      const numValue = value ? Number(value) : "";
      
      handleInputChange(row.id, "emi", numValue);
    }}
    onKeyDown={(e) => {
      // Prevent 'e', 'E', '+', '-' characters, but allow '.' once
      if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
        e.preventDefault();
      }
      
      // Prevent multiple decimal points
      if (e.key === '.' && row.emi && row.emi.toString().includes('.')) {
        e.preventDefault();
      }
    }}
    onPaste={(e) => {
      // Allow numbers and single decimal point in pasted content
      const pastedText = e.clipboardData.getData('text');
      if (!/^\d*\.?\d*$/.test(pastedText)) {
        e.preventDefault();
      }
    }}
    min="0"
    step="any"
    placeholder="Enter EMI"
    style={{ width: "100%" }}
    disabled={!editable}
  />
</Col>
          <Col xs={24} md={4}>
            {editable && (
              <Button
                type="text"
                icon="delete"
                onClick={() => removeRow(row.id)}
              />
            )}
          </Col>
        </Row>
      ))}
    </>
  );
};

export default FinanceForm;
